<script setup lang="ts">
import { ref, withDefaults, defineProps, defineEmits, watch } from 'vue';
import Api from '@/services/api/ApiServiceFabrick';
import { useAuth } from '@/services/user/Auth0UserProvider';
import { ObjectToFormData, firstFile } from '@/services/functions';
import { GDialog } from 'gitart-vue-dialog';
import outputContentsMessage from '@/types/apiresponses/jobStatusOutputContentsMessage';
import i18n from '@/lang/i18n';

const { loginWithRedirect } = useAuth();
const state = ref<boolean>(false);
const uploading = ref<boolean>(false);
const uploadFileResults = ref<outputContentsMessage>({});
const validationMessages = ref<string[]>([]);
const selectedFile = ref<File | File[] | null>([]);

type Props = {
  modalState: boolean;
  title: string;
  modalMaxWidth?: string;
  uploadBtnClass?: string;
  uploadBtnName?: string;
  closeBtnName?: string;
  closeBtnClass?: string;
  uploadFileText?: string;
  type: string;
  operationOfficeId?: number;
  notes?: string;
  sampleFileDownloadText?: string;
  templateCsvFileType?: string;
  uploadableExtension?: string;
};

const props = withDefaults(defineProps<Props>(), {
  modalState: false,
  modalMaxWidth: '50%',
  uploadBtnName: i18n.global.t('pages.common.button.import'),
  closeBtnName: i18n.global.t('pages.common.button.close'),
  uploadFileText: i18n.global.t('pages.common.labels.inputFile'),
  notes: i18n.global.t('pages.common.notes.csvImportFormat'),
  sampleFileDownloadText: i18n.global.t('pages.common.button.sampleCsvFileDownload'),
  uploadableExtension: 'text/csv',
});

watch(
  () => props.modalState,
  (value: boolean) => {
    state.value = value;
    if (state.value) return;

    uploadFileResults.value = {};
    validationMessages.value = [];
    selectedFile.value = [];
  }
);

type Emits = {
  (e: 'closeModal', value: boolean): void;
};

const emit = defineEmits<Emits>();

const uploadFile = () => {
  const file = firstFile(selectedFile.value);
  if (!file) {
    validationMessages.value = ['選択してください。'];
    return;
  }
  uploading.value = true;
  uploadFileResults.value = {};
  validationMessages.value = [];

  const params = {
    operation_office_id: props.operationOfficeId,
    type: props.type,
  };

  let formData = new FormData();
  formData.append('file', file);
  ObjectToFormData(params, formData, '');

  Api()
    .createJobStatus(formData)
    .then((data) => {
      pollingJobStatus(data.id);
    })
    .catch((error) => {
      const res = error.response;
      switch (res.status) {
        case 401:
          loginWithRedirect({ appState: { targetUrl: location.pathname } });
          break;
        case 403:
          location.reload();
          break;
        case 422:
          validationMessages.value = res.data.message['file'];
      }
      uploading.value = false;
    });
};

const pollingJobStatus = (id: number) => {
  const interval = setInterval(() => {
    Api()
      .getJobStatus(id)
      .then((res) => {
        if (res.status == 'completed') {
          uploadFileResults.value = res?.output_contents?.message ?? {};
          clearInterval(interval);
          uploading.value = false;
        }
        if (res.status == 'failed') {
          alert(res?.output_contents?.message);
          clearInterval(interval);
          uploading.value = false;
        }
        clickClearButton();
      });
  }, 3000);
};

const download = () => {
  if (!props.templateCsvFileType) return;

  Api().csvTemplatefileDownload(props.templateCsvFileType);
};

const close = () => {
  let isReacquire = false;
  const successMsg = uploadFileResults.value?.['success'];
  const updatedSuccessMsg = uploadFileResults.value?.['updatedSuccess'];

  if ((successMsg || updatedSuccessMsg)
    && (Number(successMsg.split(' ')[1]) >= 1 || Number(updatedSuccessMsg?.split(' ')[1]) >= 1)
  ) {
    isReacquire = true;
  }
  emit('closeModal', isReacquire);
};

const clickClearButton = () => {
  const clearBtn = document
    .getElementById('upload-file-wrapper')
    ?.getElementsByClassName('v-field__clearable')[0]
    ?.children[0] as HTMLElement | null;

  if (clearBtn) clearBtn.click();
};
</script>

<template>
  <GDialog
    v-if="state"
    v-model="state"
    :max-width="modalMaxWidth"
    transition="custom-from-bottom-transition"
    :persistent="true"
  >
    <div class="modal-header justify-content-center mt-2">
      <label>{{ title }}</label>
    </div>

    <div class="modal-body ml-2">
      <div class="upload-file-area mt-2">
        <div>{{ uploadFileText }}</div>
        <div
          id="upload-file-wrapper"
          class="v-theme--light mt-2"
          style="width: 80%"
        >
          <v-file-input
            label="ファイルを選択"
            v-model="selectedFile"
            truncate-length="15"
            :accept="uploadableExtension"
            :error-messages="validationMessages"
          />
        </div>
      </div>

      <div class="mt-2">{{ notes }}</div>

      <div class="mt-2" style="color: #3c8dbc" v-show="!!templateCsvFileType">
        <span class="sample-file-download" @click="download">{{
          sampleFileDownloadText
        }}</span>
      </div>
      <div>
        <slot name="supplementary-message" />
      </div>
      <div class="upload-file-results mt-2">
        <p style="color: #3c763d; margin-bottom: 0px important!">
          {{ uploadFileResults?.['success'] }}
        </p>
        <p style="color: #3c763d; margin-bottom: 0px important!">
          {{ uploadFileResults?.['updatedSuccess'] }}
        </p>
        <p class="mt-1" style="color: #a94442">
          {{ uploadFileResults?.['fail'] }}
        </p>
        <p
          v-for="(errors, msg) in uploadFileResults?.['errors'] ?? {}"
          :key="msg"
          style="color: #a94442"
        >
          {{ msg }} 行番号:{{ errors?.join(',') }}
        </p>
      </div>

      <div class="d-flex justify-content-end m-3 mt-2">
        <v-btn
          :class="closeBtnClass"
          class="close-btn"
          @click="close"
          :disabled="uploading"
        >
          {{ closeBtnName }}
        </v-btn>

        <v-btn
          :class="uploadBtnClass"
          class="file-upload-btn"
          @click="uploadFile"
          :loading="uploading"
        >
          {{ uploadBtnName }}
        </v-btn>
      </div>
    </div>
  </GDialog>
</template>

<style scoped>
.sample-file-download:hover {
  cursor: pointer;
  opacity: 0.5;
}
.upload-file-results p {
  margin-bottom: 0px !important;
}
.upload-file-area .v-messages__message {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
